import React, { memo, useCallback, useEffect, useMemo, useState } from "react";
import { NumberParam, StringParam, useQueryParams } from "use-query-params";
import { useDebounce, useToggle } from "react-use";
import useNotification from "../../hooks/useNotification";
import useTranslations from "../../hooks/useTranslations";
import useViewer from "../../hooks/useViewer";
import {
  EditIcon,
  PlusIcon,
  SortAscendingDirectionIcon,
  SortDescendingDirectionIcon,
} from "../icons";
import List from "../utils/List";
import Pagination from "../utils/Pagination";
import SearchInput from "../utils/SearchInput";
import SearchPopover from "../utils/SearchPopover";
import SearchPopoverItem from "../utils/SearchPopoverItem";
import SearchPopoverHeading from "../utils/SearchPopoverHeading";
import { Button, ButtonGroup, H6, Link } from "../wombat-ui";
import EditProfessionDialog from "./EditProfessionDialog";
import { orderBy } from "lodash";
import SkillTag from "../skills/SkillTag";
import { TagCloseButton } from "@chakra-ui/react";
import { Profession, SkillFragment, useDeleteProfessionSkill, useGetProfessions, useGetSkillsLazyQuery, useInsertProfessionSkill } from '../../graphql/generated/definitions';

const SkillSearchItem: React.FC<{
  skill: SkillFragment;
  baseId?: string;
}> = ({ skill, baseId }) => (
  <SearchPopoverItem id={`${baseId}${skill.id}`}>
    <div className="flex space-x-2">
      <p>{skill.name}</p>
    </div>
  </SearchPopoverItem>
);

const ProfessionItem = ({
  profession,
  setProfessionDialog,
  refetchProfessions,
}) => {
  const t = useTranslations();
  const { notifySuccess, notifyError } = useNotification();

  const [insertProfessionSkill] = useInsertProfessionSkill();
  const [deleteProfessionSkill] = useDeleteProfessionSkill();
  const { company } = useViewer();

  const [getSkills, { data }] = useGetSkillsLazyQuery({
    variables: {
      companyId: company.id,
      sortBy: "name",
    },
    fetchPolicy: "cache-and-network",
  });
  const skills = data?.skills?.collection || [];

  const orderedProfessionSkills = useMemo(
    () => orderBy(profession.skills, ({ name }) => name.toLocaleLowerCase()),
    [profession.skills]
  );

  const handleItemSelected = useCallback(
    async (skill: SkillFragment, emphasized: boolean) => {
      try {
        await insertProfessionSkill({
          variables: {
            input: {
              skillId: skill.id,
              professionId: profession.id,
            },
          },
        });

        notifySuccess({
          title: t(
            `professionSkillLibrary.notification.professionSkill.change.success`
          ) as string,
        });

        refetchProfessions();
      } catch {
        notifyError({
          title: t(
            `professionSkillLibrary.notification.professionSkill.change.error`
          ) as string,
        });
      }
    },
    [
      refetchProfessions,
      deleteProfessionSkill,
      insertProfessionSkill,
      profession,
    ]
  );

  const [search, setSearch] = useState("");

  // no control, it's just a flag to avoid making useless
  // requests when the popover is not open
  const [popoverOpen, togglePopoverOpen] = useToggle(false);

  useDebounce(
    () => {
      if (popoverOpen) {
        getSkills({ variables: { q: search } });
      }
    },
    200,
    [search, popoverOpen, getSkills]
  );

  // clear search on close
  useEffect(() => {
    if (!popoverOpen) {
      setSearch("");
      getSkills({ variables: { q: "" } });
    }
  }, [popoverOpen, getSkills]);

  const filteredSkills = useMemo(
    () =>
      skills.filter(
        ({ id: skillId }) =>
          !profession.skills.find(
            ({ id: professionSkillId }) => skillId === professionSkillId
          )
      ),
    [skills, profession]
  );

  const handleRemoveSkill = useCallback(
    async (skill) => {
      try {
        await deleteProfessionSkill({
          variables: {
            input: {
              skillId: skill.id,
              professionId: profession.id,
            },
          },
        });

        notifySuccess({
          title: t(
            `professionSkillLibrary.notification.professionSkill.change.success`
          ) as string,
        });

        refetchProfessions();
      } catch {
        notifyError({
          title: t(
            `professionSkillLibrary.notification.professionSkill.change.error`
          ) as string,
        });
      }
    },
    [deleteProfessionSkill, refetchProfessions, profession.id]
  );

  const havingAnswersMatching = useMemo(
    () =>
      JSON.stringify(
        profession.skills.map(({ id }) => ({
          skillId: id
        }))
      ),
    [profession.skills]
  );

  return (
    <div
      key={profession.id}
      id={`profession_library_${profession.id}`}
      className="grid items-center grid-cols-12 py-4 pt-2"
    >
      <div className="flex flex-row items-center col-span-3 gap-2 group">
        <Link
          to={`/users?havingAnswersMatching=${encodeURIComponent(
            havingAnswersMatching
          )}`}
          disabled={profession.skills.length === 0}
        >
          <p className="text-sm">{profession.name}</p>
        </Link>
        <Button
          icon={EditIcon}
          onClick={() => setProfessionDialog({ open: true, profession })}
          id={`openEditProfessionDialog_${profession.id}`}
          variant="ghost"
          scale="xs"
          className="opacity-0 group-hover:opacity-100"
        />
      </div>
      <div className="col-span-9">
        <div className="flex items-center justify-end pt-2">
          <div className="flex flex-wrap justify-end gap-2 pr-2">
            {orderedProfessionSkills.map((skill: SkillFragment) => (
              <SkillTag skill={skill} key={skill.id}>
                <TagCloseButton
                  data-test-id={`remove_badge_skill_badge_${skill.id}`}
                  onClick={() => handleRemoveSkill(skill)}
                />
              </SkillTag>
            ))}
          </div>
          {profession.canInsert && (
            <SearchPopover
              title={
                <SearchPopoverHeading>
                  {t("professionSkillLibrary.skills")}
                </SearchPopoverHeading>
              }
              loading={false}
              placeholder={t("professionSkillLibrary.filterSkills") as string}
              items={filteredSkills}
              align="bottom-right"
              onItemSelected={handleItemSelected}
              onSearchChange={setSearch}
              searchValue={search}
              noMatch={
                <SearchPopoverItem>
                  {t("wombat.select.emptyResult")}
                </SearchPopoverItem>
              }
              renderItem={(item: SkillFragment) => (
                <SkillSearchItem skill={item} baseId={profession.id} />
              )}
              button={
                <Button
                  data-test-id={`${profession.id}_add_skill`}
                  variant="ghost"
                  icon={PlusIcon}
                  onClick={togglePopoverOpen}
                />
              }
            />
          )}
        </div>
      </div>
    </div>
  );
};

interface ProfessionLibraryProps {
  locale: string;
}

const ProfessionLibrary: React.FC<ProfessionLibraryProps> = ({
  locale,
}) => {
  const t = useTranslations();
  const { isCapableOfInsertingProfessions, companyId } = useViewer();
  const [professionDialog, setProfessionDialog] = useState({
    open: false,
    profession: null,
  });

  const [{ page, professionQ, direction }, setQuery] = useQueryParams({
    page: NumberParam,
    sortBy: StringParam,
    direction: StringParam,
    professionQ: StringParam,
  });

  const [search, setSearch] = useState(professionQ);
  useEffect(() => setSearch(professionQ), [professionQ]);

  const {
    data,
    loading,
    refetch: refetchProfessions,
  } = useGetProfessions({
    variables: {
      q: professionQ,
      page: page || 1,
      per: 20,
      sortBy: "name",
      direction,
      locale,
      companyId,
    },
  });

  const professions = data?.professions?.collection || [];
  const itemsCount = data?.professions?.metadata?.totalCount || 0;

  const handleCloseProfessionDialog = useCallback(() => {
    setProfessionDialog({ open: false, profession: null });
  }, [setProfessionDialog]);

  const handleChangeProfessionDialog = useCallback(
    (result) => {
      refetchProfessions();
      handleCloseProfessionDialog();

      // auto-set the search on create
      if (!professionDialog?.profession) {
        setQuery({ professionQ: result.name });
      }
    },
    [
      refetchProfessions,
      handleCloseProfessionDialog,
      !professionDialog?.profession,
    ]
  );

  const handleDeleteProfessionDialog = useCallback(() => {
    refetchProfessions();
    handleCloseProfessionDialog();
  }, [handleCloseProfessionDialog, refetchProfessions]);

  return (
    <>
      <div className="flex flex-col space-y-4">
        <List
          isLoading={loading}
          emptyMessage={t("professionSkillLibrary.noMatchingProfessions") as string}
          data={professions}
          listOnly
          renderAsList={(data: Profession) => (
            <ProfessionItem
              profession={data}
              key={data.id}
              setProfessionDialog={setProfessionDialog}
              refetchProfessions={refetchProfessions}
            />
          )}
          id="professions-list"
        >
          <SearchInput
            onSubmit={(q) => setQuery({ page: 1, professionQ: q })}
            label={t("list.search") as string}
            placeholder={
              t("professionSkillLibrary.searchPlaceholder") as string
            }
            value={search}
            onChange={setSearch}
          />
          <div className="flex space-x-3">
            {isCapableOfInsertingProfessions && (
              <Button
                onClick={() =>
                  setProfessionDialog({ open: true, profession: null })
                }
                id="createProfessionButton"
              >
                {t("professionSkillLibrary.dialog.profession.title.create")}
              </Button>
            )}
            <ButtonGroup scale="sm">
              <Button
                icon={SortAscendingDirectionIcon}
                onClick={() => setQuery({ direction: "ASC" })}
                variant={
                  direction === "ASC" || !direction ? "primary" : undefined
                }
              />
              <Button
                icon={SortDescendingDirectionIcon}
                onClick={() => setQuery({ direction: "DESC" })}
                variant={direction === "DESC" ? "primary" : undefined}
              />
            </ButtonGroup>
          </div>
        </List>
        {!loading && (
          <Pagination
            page={page}
            onChange={(page) => setQuery({ page })}
            itemsCount={itemsCount}
            itemsPerPage={20}
          />
        )}
      </div>
      <EditProfessionDialog
        open={professionDialog.open}
        onClose={handleCloseProfessionDialog}
        onInsert={handleChangeProfessionDialog}
        onDelete={handleDeleteProfessionDialog}
        profession={professionDialog.profession}
      />
    </>
  );
};

export default memo(ProfessionLibrary);
